import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { createElement } from "react";

// In-memory store of entity labels keyed by `${kind}:${id}`. Detail pages
// (tenant, assessment, attempt) populate it in useEffect so breadcrumbs can
// show "Platform / Tenants / acme" instead of "Platform / Tenants / Tenant".
// No network traffic — the detail page already fetched the entity.

type Store = Record<string, string>;

interface Ctx {
  get: (kind: string, id: string) => string | undefined;
  set: (kind: string, id: string, label: string) => void;
}

const BreadcrumbContext = createContext<Ctx | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [store, setStore] = useState<Store>({});

  const get = useCallback(
    (kind: string, id: string) => store[`${kind}:${id}`],
    [store],
  );
  const set = useCallback((kind: string, id: string, label: string) => {
    setStore((prev) => {
      const key = `${kind}:${id}`;
      if (prev[key] === label) return prev;
      return { ...prev, [key]: label };
    });
  }, []);

  // Using createElement keeps this module .ts (not .tsx) so consumers can
  // import it without a compile-mode flip.
  return createElement(BreadcrumbContext.Provider, { value: { get, set } }, children);
}

export function useBreadcrumbCache(): Ctx {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) {
    // Safe default if provider missing — returns empty.
    return { get: () => undefined, set: () => undefined };
  }
  return ctx;
}
